<script setup lang="ts">
import { computed } from 'vue';

interface Props {
    value?: number; // 0-100
    class?: string;
    color?: 'positive' | 'negative' | 'terracotta' | 'info' | 'alert' | 'muted';
    /** Nom accessible de la barre (ex. « Progression du budget Courses »). */
    label?: string;
    /** Valeur lue par les lecteurs d'écran (ex. « 117 % du plafond utilisé »). */
    valueText?: string;
}

const props = withDefaults(defineProps<Props>(), {
    value: 0,
    color: 'positive',
    label: undefined,
    valueText: undefined,
});

// Une valeur absente ou invalide (NaN, Infinity) donne une barre vide.
const clamped = computed(() =>
    Number.isFinite(props.value) ? Math.min(100, Math.max(0, props.value)) : 0,
);

const barClass = computed(() => {
    switch (props.color) {
        case 'negative':
            return 'bg-negative';
        case 'terracotta':
            return 'bg-terracotta';
        case 'info':
            return 'bg-info';
        case 'alert':
            return 'bg-alert';
        case 'muted':
            return 'bg-muted';
        case 'positive':
        default:
            return 'bg-positive';
    }
});
</script>

<template>
    <!-- Progress bar per design: thin 8px, rounded-4, soft bg track, semantic fill -->
    <div
        role="progressbar"
        :aria-valuenow="clamped"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="label"
        :aria-valuetext="valueText"
        :class="[
            'bg-surface-2 border-border/50 h-2 w-full overflow-hidden rounded-full border dark:bg-[#2c261f]',
            props.class,
        ]"
    >
        <div
            :class="[
                'h-full rounded-full transition-all duration-300 ease-out',
                barClass,
            ]"
            :style="{ width: clamped + '%' }"
        />
    </div>
</template>
